import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  glow?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading, glow, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 rounded-lg focus-glow disabled:opacity-50 disabled:cursor-not-allowed',
          // Variants
          variant === 'primary' && [
            'bg-primary text-primary-foreground hover:bg-primary/90',
            glow && 'glow-primary',
          ],
          variant === 'secondary' && [
            'bg-secondary text-foreground border border-border hover:border-primary/30 hover:bg-secondary/80',
          ],
          variant === 'danger' && [
            'bg-destructive text-destructive-foreground hover:bg-destructive/90',
            glow && 'glow-destructive',
          ],
          variant === 'ghost' && [
            'text-foreground hover:bg-secondary',
          ],
          variant === 'outline' && [
            'border border-border bg-transparent text-foreground hover:bg-secondary hover:border-primary/30',
          ],
          // Sizes
          size === 'sm' && 'px-3 py-1.5 text-sm',
          size === 'md' && 'px-4 py-2 text-sm',
          size === 'lg' && 'px-6 py-3 text-base',
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
